// In-app copy of sdk/README.md - documents the standalone JS/TS package
// another web app installs to run the same crypto client-side, without
// pulling in this app's UI/session wiring.
import { el } from "./dom";
import { renderSnippetTabs } from "./codeSnippets";
import type { TabId } from "../main";

function pageLink(label: string, tab: TabId, onNavigate: (tab: TabId) => void): HTMLElement {
  const btn = el("button", { type: "button", class: "link-btn" }, [label]);
  btn.addEventListener("click", () => onNavigate(tab));
  return btn;
}

export function renderSdkPanel(onNavigate: (tab: TabId) => void): HTMLElement {
  const integrationLink = pageLink("Integration guide", "integration", onNavigate);

  return el("section", {}, [
    el("p", { class: "eyebrow" }, ["Developer · SDK"]),
    el("h2", {}, ["secure-crypt-sdk"]),
    el("p", { class: "lede" }, [
      "The crypto primitives from secure-crypt, packaged standalone so another application can run the same zero-knowledge encryption client-side, without pulling in the whole web app. Every function here runs in a browser main thread, a Web Worker, or Node -- no server involvement, no network calls.",
    ]),

    el("div", { class: "card prose" }, [
      el("p", {}, [
        "This package does not include this app's own UI/session wiring (the worker RPC client, API token handling, HTTP API client) -- those are specific to this app's UI, not reusable crypto.",
      ]),
      el("p", { class: "warning" }, [
        el("strong", {}, ["Node caveat: "]),
        "file encryption/decryption take and return Web Streams (",
        el("code", {}, ["ReadableStream<Uint8Array>"]),
        "). Recent Node versions support these natively (",
        el("code", {}, ["stream/web"]),
        "); older Node needs a polyfill.",
      ]),
      el("h3", {}, ["Install"]),
      renderSnippetTabs({ sdk: `npm install secure-crypt-sdk` }),
    ]),

    el("p", { class: "section-label" }, ["Generate a keypair"]),
    el("div", { class: "card prose" }, [
      renderSnippetTabs({
        sdk:
          `import { generateKeypair, wrapPrivateKey, unwrapPrivateKey } from "secure-crypt-sdk";\n\n` +
          `const { publicKey, privateKey } = await generateKeypair();\n` +
          `// Persist privateKey encrypted at rest -- never store it raw:\n` +
          `const wrapped = await wrapPrivateKey(privateKey, passphraseBytes);\n` +
          `// ...later:\n` +
          `const recoveredPrivateKey = await unwrapPrivateKey(wrapped, passphraseBytes);`,
      }),
    ]),

    el("p", { class: "section-label" }, ["Share a file with a recipient's public key (read-only style sharing)"]),
    el("div", { class: "card prose" }, [
      el("p", {}, [
        "This is the composition for \"app shares a PDF, recipient opens it with their public key\" -- a random one-time password is generated for you, used to encrypt the file, and sealed to the recipient's public key. You never see or handle that password yourself.",
      ]),
      renderSnippetTabs({
        sdk:
          `import { encryptFileForRecipient, decryptFileForRecipientPrepare } from "secure-crypt-sdk";\n\n` +
          `// Sender side:\n` +
          `const { chunks, envelope } = await encryptFileForRecipient(fileStream, recipientPublicKey);\n` +
          `// \`chunks\` is the encrypted container (stream it to disk/network);\n` +
          `// \`envelope\` is small (~100 bytes) -- deliver both to the recipient\n` +
          `// through whatever channel your app already uses. Neither is sent\n` +
          `// anywhere by this SDK.\n\n` +
          `// Recipient side (has their own privateKey/publicKey unlocked):\n` +
          `const handle = await decryptFileForRecipientPrepare(containerStream, envelope, privateKey, publicKey);\n` +
          `const plaintextChunks: Uint8Array[] = [];\n` +
          `for await (const chunk of handle.chunks()) plaintextChunks.push(chunk);`,
      }),
      el("p", { class: "warning" }, [
        "Only set ",
        el("code", {}, ["type"]),
        " to something that CANNOT execute script as a top-level document -- a ",
        el("code", {}, ["blob:"]),
        " URL is same-origin as your app, so opening a decrypted file with, say, ",
        el("code", {}, ["text/html"]),
        " or ",
        el("code", {}, ["image/svg+xml"]),
        " lets a maliciously crafted file run script with access to YOUR app's own storage (cookies, localStorage, IndexedDB). Stick to inert types: ",
        el("code", {}, ["application/pdf"]),
        " (renders in the browser's sandboxed built-in viewer), ",
        el("code", {}, ["image/png|jpeg|gif|webp"]),
        ", ",
        el("code", {}, ["text/plain"]),
        ", ",
        el("code", {}, ["application/json"]),
        ". Anything else should just be downloaded (e.g. via an ",
        el("code", {}, ["<a download>"]),
        " click), never opened as a top-level navigation. secure-crypt's own web app enforces this exact allowlist.",
      ]),
      renderSnippetTabs({
        sdk:
          `const blob = new Blob(plaintextChunks, { type: "application/pdf" }); // example: known-safe type\n\n` +
          `// "View" instead of "unlock and keep a copy": open the blob via a real\n` +
          `// anchor click (target="_blank"), not window.open(url, "_blank") -- current\n` +
          `// Chromium blocks scripted window.open navigation to a blob: URL created in\n` +
          `// a different browsing context, but a genuine anchor click still works.\n` +
          `const url = URL.createObjectURL(blob);\n` +
          `const a = document.createElement("a");\n` +
          `a.href = url;\n` +
          `a.target = "_blank";\n` +
          `a.rel = "noopener";\n` +
          `document.body.append(a);\n` +
          `a.click();\n` +
          `a.remove();\n` +
          `// Revoke once the viewer no longer needs it (or on a bounded timeout --\n` +
          `// otherwise the decrypted content stays reachable via this URL for the\n` +
          `// rest of the page's lifetime):\n` +
          `// URL.revokeObjectURL(url);`,
      }),
      el("p", {}, [
        el("strong", {}, ["On \"read-only\": "]),
        "this only means the calling application chooses not to offer a download button -- once a file is decrypted into the recipient's browser tab, a technically capable recipient can still extract the plaintext (e.g. via dev tools). This is not DRM and shouldn't be presented to end users as unbreakable; it just avoids leaving a saved decrypted copy lying around by default.",
      ]),
    ]),

    el("p", { class: "section-label" }, ["Sharing with more than one recipient"]),
    el("div", { class: "card prose" }, [
      el("p", {}, [
        el("code", {}, ["encryptFileForRecipients"]),
        " (plural) does the same thing for a whole group at once -- one independent one-time password and envelope per recipient, so revoking one person later never means re-encrypting the file or affecting anyone else's access (it's just removing their password slot).",
      ]),
      renderSnippetTabs({
        sdk:
          `import { encryptFileForRecipients, decryptFileForRecipientPrepare } from "secure-crypt-sdk";\n\n` +
          `const { chunks, envelopes } = await encryptFileForRecipients(fileStream, {\n` +
          `  alice: alicePublicKey,\n` +
          `  bob: bobPublicKey,\n` +
          `});\n` +
          `// \`envelopes.alice\` and \`envelopes.bob\` are independent -- deliver each\n` +
          `// recipient only their own envelope, plus the one shared encrypted\n` +
          `// container. Either recipient decrypts exactly like the single-recipient\n` +
          `// case above, using their own private key and their own envelope:\n` +
          `const handle = await decryptFileForRecipientPrepare(containerStream, envelopes.bob, bobPrivateKey, bobPublicKey);`,
      }),
      el("p", {}, [
        el("code", {}, ["encryptFileForRecipient"]),
        " (singular, above) is just this called with one recipient -- use whichever reads better for your call site.",
      ]),
    ]),

    el("p", { class: "section-label" }, ["Password-based file encryption (multiple independent passwords)"]),
    el("div", { class: "card prose" }, [
      renderSnippetTabs({
        sdk:
          `import { encryptFile, decryptFilePrepare } from "secure-crypt-sdk";\n\n` +
          `const container = encryptFile(fileStream, { alice: alicePasswordBytes, bob: bobPasswordBytes });\n` +
          `// ...\n` +
          `const handle = await decryptFilePrepare(containerStream, somePasswordBytes); // any one slot's password`,
      }),
    ]),

    el("p", { class: "section-label" }, ["Short strings/messages for a public key"]),
    el("div", { class: "card prose" }, [
      renderSnippetTabs({
        sdk:
          `import { encryptData, decryptData } from "secure-crypt-sdk";\n\n` +
          `const ciphertext = await encryptData(plaintextBytes, recipientPublicKey);\n` +
          `const plaintext = await decryptData(ciphertext, myPrivateKey, myPublicKey);`,
      }),
    ]),

    el("p", { class: "section-label" }, ["Optional: relaying containers/public keys over HTTP"]),
    el("div", { class: "card prose" }, [
      el("p", {}, [
        "If you want to store/relay encrypted containers or publish/fetch a recipient's public key rather than handling that yourself, secure-crypt's own service exposes two endpoints intended for exactly this -- see the ",
        integrationLink,
        " page. Those endpoints never see plaintext, passwords, private keys, or the envelope from the \"share a file\" flow above -- the envelope is your application's responsibility to deliver.",
      ]),
    ]),
  ]);
}
